/**
 * demoRedactor.js — the deep PII walker at the heart of demo mode.
 *
 * `redact(value)` returns a copy of any API payload with every personally
 * identifiable field replaced by a deterministic synthetic value
 * ({@link module:app/demo/demoFaker}). When demo mode is OFF it is a zero-cost
 * identity function. It NEVER mutates its input and NEVER throws — on any
 * unexpected shape it returns the original value untouched.
 *
 * Design principles:
 *   • Allow-list, not deny-list. Only known PII keys are ever rewritten; ids,
 *     dates, amounts, statuses, codes, tokens and — crucially — product / menu
 *     names, prices and SKUs pass through unchanged. The demo menu stays real.
 *   • Context-aware `name`. A bare `name` is PII on a customer/guest/partner but
 *     a product title on a food/category. It is only faked when the surrounding
 *     object (or the key that holds it) is "person-ish"; otherwise it is left.
 *   • Deterministic + consistent. All values come from demoFaker, so the same
 *     real person looks identical across every module and realtime channel.
 *
 * @module app/demo/demoRedactor
 */

import {isDemoMode} from './demoState';
import {
    fakeAddress,
    fakeCity,
    fakeCompany,
    fakeEmail,
    fakeFirstName,
    fakeFullName,
    fakeHouseNumber,
    fakeIban,
    fakeLastName,
    fakePhone,
    fakePlaceName,
    fakeStreet,
    fakeTaxNumber,
    fakeVenueName,
    fakeZip,
} from './demoFaker';

const MAX_DEPTH = 8;

/** Scalar PII keys that are ALWAYS faked regardless of surrounding context. */
const ALWAYS = {
    email: fakeEmail,
    emailaddress: fakeEmail,
    'e-mail': fakeEmail,
    phone: fakePhone,
    phonenumber: fakePhone,
    mobile: fakePhone,
    mobilenumber: fakePhone,
    tel: fakePhone,
    telefon: fakePhone,
    telephone: fakePhone,
    firstname: fakeFirstName,
    keresztnev: fakeFirstName,
    lastname: fakeLastName,
    vezeteknev: fakeLastName,
    companyname: fakeCompany,
    cegnev: fakeCompany,
    taxnumber: fakeTaxNumber,
    taxid: fakeTaxNumber,
    vatnumber: fakeTaxNumber,
    adoszam: fakeTaxNumber,
    iban: fakeIban,
    bankaccount: fakeIban,
    bankaccountnumber: fakeIban,
    street: fakeStreet,
    utca: fakeStreet,
    city: fakeCity,
    varos: fakeCity,
    town: fakeCity,
    zip: fakeZip,
    zipcode: fakeZip,
    postcode: fakeZip,
    postalcode: fakeZip,
    irsz: fakeZip,
    housenumber: fakeHouseNumber,
    hazszam: fakeHouseNumber,
    contactname: fakeFullName,
    contactperson: fakeFullName,
};

/** `name`-like keys — faked ONLY in a person-ish context (else product title). */
const GATED_NAME = new Set(['name', 'fullname', 'displayname', 'guestname', 'customername',
    'partnername', 'clientname', 'sendername', 'recipientname', 'holdername', 'cardholdername',
    'nev', 'ordername']);

/** Sub-keys of a `name` object that carry given/family names. */
const FIRST_KEYS = new Set(['first', 'firstname', 'keresztnev', 'given', 'givenname']);
const LAST_KEYS = new Set(['last', 'lastname', 'vezeteknev', 'family', 'familyname', 'surname']);

/** Keys whose VALUE (object/array) holds a person/partner — propagates person-ish. */
const PERSON_KEY = new Set(['customer', 'customers', 'guest', 'guests', 'user', 'userinfo',
    'contact', 'contacts', 'partner', 'partners', 'supplier', 'vendor', 'client', 'recipient',
    'sender', 'billingdata', 'billing', 'invoicedata', 'driver', 'courier', 'assignee',
    'createdby', 'updatedby', 'owner', 'salesperson', 'technician', 'mechanic', 'member',
    'holder', 'cardholder', 'person', 'people', 'admins', 'admin', 'staff', 'employee',
    'employees', 'vipcard', 'vipcards', 'lead', 'leads']);

/** Keys that mark an object as a person even without a person-ish parent key. */
const PERSONISH_MARKER = ['email', 'phone', 'mobile', 'tel', 'telefon', 'firstname', 'lastname',
    'keresztnev', 'vezeteknev', 'iscompany', 'companyname', 'taxnumber', 'taxid', 'vatnumber',
    'phonenumbers', 'phonenumber', 'contact'];

/** Keys whose VALUE holds a RESTAURANT / store / venue — its `name` is faked as a venue. */
const VENUE_KEY = new Set(['restaurant', 'restaurants', 'venue', 'venues', 'store', 'stores',
    'branch', 'branches', 'warehouse', 'warehouses', 'shop', 'unit']);

/** Keys whose VALUE holds a PLACE / area / section — its `name` is faked as a place. */
const PLACE_KEY = new Set(['place', 'places', 'restaurantplace', 'section', 'sections',
    'room', 'rooms', 'area', 'areas', 'terminal']);

/**
 * Classify an object as a venue/place even without a venue/place parent key
 * (e.g. a bare `{ name, places[] }` restaurant-detail response). Deliberately
 * conservative so it never mistakes a food/category for a venue.
 * @param {object} obj candidate object.
 * @returns {'venue'|'place'|null} the venue kind, or null.
 */
function venueKindOf(obj) {
    if (!obj || typeof obj !== 'object') {
        return null;
    }
    if (Array.isArray(obj.places) || obj.openingHours || obj.opening_hours) {
        return 'venue';
    }
    // A place chooser object: `{ _id, name, raw, code }` with a table/place marker.
    if (('raw' in obj) && (obj.code !== undefined || obj.tableCount !== undefined || obj.isPlace)) {
        return 'place';
    }

    return null;
}

/**
 * Does this object look like a person/partner record (so its `name` is PII)?
 * @param {object} obj candidate object.
 * @returns {boolean} true when a person-ish marker key is present.
 */
function isPersonish(obj) {
    if (!obj || typeof obj !== 'object') {
        return false;
    }
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i++) {
        if (PERSONISH_MARKER.includes(keys[i].toLowerCase())) {
            return true;
        }
    }

    return false;
}

/**
 * Should a person-ish `name` be a company rather than a personal name?
 * @param {object} obj the owning object.
 * @param {string} key the key that referenced it (e.g. 'partner').
 * @returns {boolean} true → use a company name.
 */
function isCompanyEntity(obj, key) {
    const k = String(key || '').toLowerCase();
    if (k === 'partner' || k === 'supplier' || k === 'vendor' || k === 'company') {
        return true;
    }
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    return Boolean(obj.isCompany || obj.companyName ||
        String(obj.customerType || '').toLowerCase() === 'company' ||
        String(obj.type || '').toLowerCase() === 'company');
}

/**
 * Redact a `name` object such as `{first,last}` or a localized `{hu,en}` map.
 * @param {object} value the name object.
 * @param {object} owner the object that holds this name.
 * @param {boolean} personish whether the owner is a person/partner.
 * @param {('venue'|'place'|null)} [venueKind] venue context, if any.
 * @returns {object} a redacted copy.
 */
function redactNameObject(value, owner, personish, venueKind) {
    const out = Array.isArray(value) ? [...value] : {...value};
    const asCompany = isCompanyEntity(owner);
    Object.keys(out).forEach((sub) => {
        const sk = sub.toLowerCase();
        const v = out[sub];
        if (typeof v !== 'string') {
            return;
        }
        if (FIRST_KEYS.has(sk)) {
            out[sub] = fakeFirstName(v);
        } else if (LAST_KEYS.has(sk)) {
            out[sub] = fakeLastName(v);
        } else if (venueKind === 'place') {
            out[sub] = fakePlaceName(v);
        } else if (venueKind === 'venue') {
            out[sub] = fakeVenueName(v);
        } else if (personish) {
            // localized name variant (hu/en/de/…) on a person/partner → full/company name
            out[sub] = asCompany ? fakeCompany(v) : fakeFullName(v);
        }
    });

    return out;
}

/**
 * Try to redact a single field by name. Returns `undefined` when the field is
 * not directly handled (caller should recurse into it instead).
 * @param {string} key field name.
 * @param {*} value field value.
 * @param {object} obj owning object.
 * @param {boolean} parentPersonish whether the owning object is person-ish.
 * @param {('venue'|'place'|null)} parentVenueKind venue context inherited from the parent key.
 * @returns {*} the redacted value, or `undefined` to signal "recurse".
 */
function redactField(key, value, obj, parentPersonish, parentVenueKind) {
    const k = String(key).toLowerCase();

    if (ALWAYS[k] && (typeof value === 'string' || typeof value === 'number')) {
        return String(value).length ? ALWAYS[k](value) : value;
    }

    if (k === 'phonenumbers' && Array.isArray(value)) {
        return value.map((v) => (typeof v === 'string' ? fakePhone(v) : v));
    }

    if (GATED_NAME.has(k)) {
        const venueKind = parentVenueKind || venueKindOf(obj);
        const personish = parentPersonish || isPersonish(obj);
        if (typeof value === 'string') {
            if (!value.length) {
                return value;
            }
            if (venueKind === 'place') {
                return fakePlaceName(value); // restaurant place / area / section name
            }
            if (venueKind === 'venue') {
                return fakeVenueName(value); // restaurant / store / warehouse name
            }
            if (!personish) {
                return value; // product / category title — leave real
            }

            return isCompanyEntity(obj, key) ? fakeCompany(value) : fakeFullName(value);
        }
        if (value && typeof value === 'object' && !(value instanceof Date)) {
            const hasNameParts = Object.keys(value).some((s) => {
                const sk = s.toLowerCase();

                return FIRST_KEYS.has(sk) || LAST_KEYS.has(sk);
            });
            if (venueKind || hasNameParts || personish) {
                return redactNameObject(value, obj, personish, venueKind);
            }
        }
    }

    if (k === 'address' && typeof value === 'string' && value.length) {
        return fakeAddress(value);
    }

    return undefined;
}

/**
 * Recursively redact any value.
 * @param {*} value input.
 * @param {boolean} parentPersonish whether the parent key marks a person context.
 * @param {('venue'|'place'|null)} parentVenueKind whether the parent key marks a venue/place context.
 * @param {WeakSet<object>} seen cycle guard.
 * @param {number} depth recursion depth.
 * @returns {*} a redacted copy (or the input for primitives).
 */
function redactAny(value, parentPersonish, parentVenueKind, seen, depth) {
    if (value == null || depth > MAX_DEPTH) {
        return value;
    }

    if (Array.isArray(value)) {
        return value.map((item) => redactAny(item, parentPersonish, parentVenueKind, seen, depth + 1));
    }

    if (typeof value !== 'object' || value instanceof Date) {
        return value;
    }

    if (seen.has(value)) {
        return value;
    }
    seen.add(value);

    const out = {};
    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const child = value[key];

        const handled = redactField(key, child, value, parentPersonish, parentVenueKind);
        if (handled !== undefined) {
            out[key] = handled;
            continue;
        }

        const lk = String(key).toLowerCase();
        const childPersonish = PERSON_KEY.has(lk);
        const childVenueKind = PLACE_KEY.has(lk) ? 'place' : (VENUE_KEY.has(lk) ? 'venue' : null);
        out[key] = redactAny(child, childPersonish, childVenueKind, seen, depth + 1);
    }

    return out;
}

/**
 * Redact a payload for display when demo mode is on. Identity no-op otherwise.
 * @param {*} value any API payload (object / array / primitive).
 * @returns {*} the redacted payload, or the input unchanged.
 */
export function redact(value) {
    if (!isDemoMode()) {
        return value;
    }
    try {
        return redactAny(value, false, null, new WeakSet(), 0);
    } catch (_) {
        return value;
    }
}

/**
 * Redact the signed-in user for display (name / e-mail / phone), while NEVER
 * touching auth-critical fields (`_id`, `roles`, `permissions`, `restaurants`
 * are only recursed for nested PII, never for their own identity). Forces a
 * person context so the top-level `name` is always faked.
 * @param {object} user the current user object.
 * @returns {object} a redacted copy for display.
 */
export function redactCurrentUser(user) {
    if (!isDemoMode() || !user || typeof user !== 'object') {
        return user;
    }
    try {
        return redactAny(user, true, null, new WeakSet(), 0);
    } catch (_) {
        return user;
    }
}

/**
 * Fake the DISPLAY name (and `raw` fallback) of a stored `restaurant` /
 * `restaurantPlace` AsyncStorage object, while preserving every id / scope /
 * config field intact. This is what the AsyncStorage interceptor uses so the
 * venue name shown in every header, hero and scope label is anonymised — those
 * two keys are read raw from storage in a dozen screens and never pass through
 * an HTTP response, so the transport proxy alone can't reach them.
 *
 * `_id` / `id` / `places` structure / `code` are left untouched so tenant
 * scoping (`x-restaurant-id`, `placeId`) keeps working exactly as before.
 * @param {'restaurant'|'restaurantPlace'} kind which stored key this is.
 * @param {object} obj the parsed stored object.
 * @returns {object} a copy with a faked venue/place name (identity no-op when off).
 */
export function redactVenueStored(kind, obj) {
    if (!isDemoMode() || !obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return obj;
    }
    try {
        const out = {...obj};
        const asPlace = kind === 'restaurantPlace';
        if (typeof out.name === 'string' && out.name.length) {
            out.name = asPlace ? fakePlaceName(out.name) : fakeVenueName(out.name);
        }
        if (typeof out.raw === 'string' && out.raw.length) {
            out.raw = asPlace ? fakePlaceName(out.raw) : fakeVenueName(out.raw);
        }

        return out;
    } catch (_) {
        return obj;
    }
}
